// Full icon set ported from design-handoff/src/icons.jsx.
// Lucide-style, 1.5px stroke, 18px default — match handoff visual weight.

import type { CSSProperties, ReactNode, SVGProps } from "react";

export type IconProps = {
  size?: number;
  stroke?: number;
  style?: CSSProperties;
  className?: string;
} & Omit<SVGProps<SVGSVGElement>, "stroke" | "style" | "className">;

function Icon({ size = 18, stroke = 1.5, style, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
      {...rest}
    >
      {children}
    </svg>
  );
}

export const Icons = {
  Home: (p: IconProps) => (<Icon {...p}><path d="M3 9.5 12 3l9 6.5V21H3z" /><path d="M9 21v-7h6v7" /></Icon>),
  Calendar: (p: IconProps) => (<Icon {...p}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></Icon>),
  Sparkles: (p: IconProps) => (<Icon {...p}><path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" /><path d="M19 16l.7 1.8L21.5 18l-1.8.7L19 20.5l-.7-1.8L16.5 18l1.8-.7z" /></Icon>),
  Layers: (p: IconProps) => (<Icon {...p}><path d="M12 3 2 8l10 5 10-5z" /><path d="M2 14l10 5 10-5" /><path d="M2 11l10 5 10-5" /></Icon>),
  Inbox: (p: IconProps) => (<Icon {...p}><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5h13l3 7v6a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-6z" /></Icon>),
  BarChart: (p: IconProps) => (<Icon {...p}><path d="M3 3v18h18" /><path d="M7 16V9M12 16V5M17 16v-4" /></Icon>),
  Settings: (p: IconProps) => (<Icon {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></Icon>),
  Plug: (p: IconProps) => (<Icon {...p}><path d="M9 2v6M15 2v6M5 8h14v3a7 7 0 0 1-14 0z" /><path d="M12 18v4" /></Icon>),
  Shield: (p: IconProps) => (<Icon {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Icon>),
  Briefcase: (p: IconProps) => (<Icon {...p}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></Icon>),
  Search: (p: IconProps) => (<Icon {...p}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></Icon>),
  Bell: (p: IconProps) => (<Icon {...p}><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></Icon>),
  Plus: (p: IconProps) => (<Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>),
  Check: (p: IconProps) => (<Icon {...p}><path d="m20 6-11 11-5-5" /></Icon>),
  X: (p: IconProps) => (<Icon {...p}><path d="M18 6 6 18M6 6l12 12" /></Icon>),
  Upload: (p: IconProps) => (<Icon {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></Icon>),
  ChevronDown: (p: IconProps) => (<Icon {...p}><path d="m6 9 6 6 6-6" /></Icon>),
  ChevronRight: (p: IconProps) => (<Icon {...p}><path d="m9 6 6 6-6 6" /></Icon>),
  ChevronLeft: (p: IconProps) => (<Icon {...p}><path d="m15 6-6 6 6 6" /></Icon>),
  ChevronUp: (p: IconProps) => (<Icon {...p}><path d="m6 15 6-6 6 6" /></Icon>),
  More: (p: IconProps) => (<Icon {...p}><circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /></Icon>),
  Image: (p: IconProps) => (<Icon {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></Icon>),
  Edit: (p: IconProps) => (<Icon {...p}><path d="M11 4H4v16h16v-7" /><path d="m18.5 2.5 3 3L12 15l-4 1 1-4z" /></Icon>),
  Trash: (p: IconProps) => (<Icon {...p}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></Icon>),
  Refresh: (p: IconProps) => (<Icon {...p}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Icon>),
  Send: (p: IconProps) => (<Icon {...p}><path d="m22 2-7 20-4-9-9-4z" /></Icon>),
  Filter: (p: IconProps) => (<Icon {...p}><path d="M3 4h18l-7 8v6l-4 2v-8z" /></Icon>),
  ArrowRight: (p: IconProps) => (<Icon {...p}><path d="M5 12h14M13 5l7 7-7 7" /></Icon>),
  ArrowUp: (p: IconProps) => (<Icon {...p}><path d="M12 19V5M5 12l7-7 7 7" /></Icon>),
  Eye: (p: IconProps) => (<Icon {...p}><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></Icon>),
  Globe: (p: IconProps) => (<Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></Icon>),
  Sliders: (p: IconProps) => (<Icon {...p}><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" /><path d="M1 14h6M9 8h6M17 16h6" /></Icon>),
  Clock: (p: IconProps) => (<Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></Icon>),
  Hash: (p: IconProps) => (<Icon {...p}><path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" /></Icon>),
  Users: (p: IconProps) => (<Icon {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></Icon>),
  Zap: (p: IconProps) => (<Icon {...p}><path d="M13 2 3 14h7l-1 8 10-12h-7z" /></Icon>),
  Play: (p: IconProps) => (<Icon {...p}><path d="M5 3v18l15-9z" /></Icon>),
  Pause: (p: IconProps) => (<Icon {...p}><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></Icon>),
  Cmd: (p: IconProps) => (<Icon {...p}><path d="M18 3a3 3 0 1 0 0 6h-3V6a3 3 0 0 0-3-3 3 3 0 0 0 0 6h3v6h-3a3 3 0 1 0 3 3v-3h3a3 3 0 1 0-3-3M9 9v6" /><path d="M15 9h-6" /></Icon>),
  Sun: (p: IconProps) => (<Icon {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></Icon>),
  Moon: (p: IconProps) => (<Icon {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></Icon>),
  Alert: (p: IconProps) => (<Icon {...p}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></Icon>),
  Info: (p: IconProps) => (<Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></Icon>),
  Folder: (p: IconProps) => (<Icon {...p}><path d="M3 5a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></Icon>),
  Type: (p: IconProps) => (<Icon {...p}><path d="M4 7V5h16v2M9 20h6M12 5v15" /></Icon>),
  Palette: (p: IconProps) => (<Icon {...p}><path d="M12 22a10 10 0 1 1 10-10c0 2.7-2 5-4.5 5H15a2 2 0 0 0-2 2c0 1.5 1 1.5 1 3a2 2 0 0 1-2 2z" /><circle cx="7.5" cy="11.5" r="1" /><circle cx="11.5" cy="7.5" r="1" /><circle cx="16.5" cy="11.5" r="1" /></Icon>),
  Mic: (p: IconProps) => (<Icon {...p}><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></Icon>),
  Download: (p: IconProps) => (<Icon {...p}><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></Icon>),
  Lock: (p: IconProps) => (<Icon {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Icon>),
  Building: (p: IconProps) => (<Icon {...p}><path d="M3 21V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v14M13 9h6a2 2 0 0 1 2 2v10" /><path d="M7 9h2M7 13h2M7 17h2M17 13h0M17 17h0" /></Icon>),
  GripVertical: (p: IconProps) => (<Icon {...p}><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></Icon>),
  Heart: (p: IconProps) => (<Icon {...p}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1 7.8 7.8 7.8-7.8 1-1.1a5.5 5.5 0 0 0 0-7.8z" /></Icon>),
  MessageCircle: (p: IconProps) => (<Icon {...p}><path d="M21 11.5a8.4 8.4 0 0 1-12.5 7.4L3 21l2.1-5.5A8.4 8.4 0 1 1 21 11.5z" /></Icon>),
};

// Platform glyphs
export function PlatformIcon({ platform, size = 14 }: { platform: string; size?: number }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "currentColor" } as const;
  switch (platform) {
    case "instagram":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="17.5" cy="6.5" r="1" />
        </svg>
      );
    case "facebook":
      return (
        <svg {...common}>
          <path d="M13 22V13h3l.5-4H13V6.5c0-1.2.3-2 2-2H17V1.1A28 28 0 0 0 14 1c-3 0-5 1.8-5 5v3H6v4h3v9z" />
        </svg>
      );
    case "x":
      return (
        <svg {...common}>
          <path d="M18 3h3l-7 8 8 10h-6l-5-6-6 6H2l8-9L2 3h6l4 5z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg {...common}>
          <rect x="2" y="9" width="4" height="12" />
          <rect x="2" y="2" width="4" height="4" rx="2" />
          <path d="M9 21V9h4v2c1-1.5 3-2.5 5-2.5 3 0 5 2 5 5V21h-4v-7c0-1.5-1-2.5-2.5-2.5S13 12.5 13 14v7z" />
        </svg>
      );
    case "telegram":
      return (
        <svg {...common}>
          <path d="M22 3 2 11l6 2 2 7 4-4 7 5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

// Anatolian rosette spinner
export function Rosette({
  size = 80,
  stroke = "currentColor",
  strokeWidth = 1,
  opacity = 1,
  spin = false,
}: {
  size?: number;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  spin?: boolean;
}) {
  return (
    <svg width={size} height={size} viewBox="-60 -60 120 120" style={{ opacity }} className={spin ? "rosette" : ""}>
      <g fill="none" stroke={stroke} strokeWidth={strokeWidth}>
        <circle r="50" />
        <circle r="36" />
        <path d="M 0,-50 L 14.7,-14.7 L 50,0 L 14.7,14.7 L 0,50 L -14.7,14.7 L -50,0 L -14.7,-14.7 Z" />
        <path d="M 0,-36 L 25.5,0 L 0,36 L -25.5,0 Z" />
        <line x1="-50" y1="0" x2="50" y2="0" />
        <line x1="0" y1="-50" x2="0" y2="50" />
        <line x1="-35.4" y1="-35.4" x2="35.4" y2="35.4" />
        <line x1="-35.4" y1="35.4" x2="35.4" y2="-35.4" />
      </g>
    </svg>
  );
}

// ND brand mark — geometric N glyph + dot
export function NDMark({ size = 28, color = "#E02D1C" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <path d="M6 34 L6 6 L14 6 L26 26 L26 6 L34 6 L34 34 L26 34 L14 14 L14 34 Z" fill={color} />
      <circle cx="32" cy="10" r="2.5" fill={color} />
    </svg>
  );
}
